import axios from 'axios';
import appConstants from '../appConstants';
import {
  firebaseMaster,
  firestoreOperationsDb,
  masterDb,
} from '../firebase';
import { matchesLocationFilter } from '../utils/geo/serviceAreaMatcher';

/**
 * Public API wrappers for the Provider Directory page.
 *
 * Distinct from src/APIs/ProviderDirectory.js (the authenticated wrappers).
 * The public browsing path reads the server-managed public projection directly
 * from Firestore to avoid Cloud Function cold starts. Access is gated in the
 * browser using the embed token plus the clinic directory enable flags.
 *
 * Each wrapper accepts an optional AbortSignal so callers can cancel an
 * in-flight request when the user types a new query / unmounts the page.
 */

const url = appConstants.firebaseFunctionsUrl;
export const PUBLIC_DIRECTORY_PAGE_SIZE = 20;
const PUBLIC_DIRECTORY_API_TIMEOUT_MS = 5000;
const PUBLIC_DIRECTORY_CONFIRM_TIMEOUT_MS = 20000;
const MAX_PAGE_SIZE = 50;
const FILTERED_BATCH_SIZE = 100;
const MAX_FILTERED_SCAN_BATCHES = 20;
const directoryBootstrapPrefetch = new Map();

function clampLimit(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return PUBLIC_DIRECTORY_PAGE_SIZE;
  return Math.min(Math.max(1, Math.floor(num)), MAX_PAGE_SIZE);
}

function makeAbortError() {
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

function throwIfAborted(signal) {
  if (signal && signal.aborted) throw makeAbortError();
}

function isAbortLikeError(error) {
  return error
    && (
      error.name === 'AbortError'
      || error.name === 'CanceledError'
      || error.code === 'ERR_CANCELED'
    );
}

async function postPublicDirectoryEndpoint(
  endpoint,
  payload,
  signal,
  timeoutMs = PUBLIC_DIRECTORY_API_TIMEOUT_MS
) {
  const res = await axios.post(`${url}${endpoint}`, payload, {
    signal,
    timeout: timeoutMs,
  });
  return res.data;
}

async function withEventsApiFallback(endpoint, payload, signal, readFromFirestore) {
  try {
    return await readFromFirestore();
  } catch (error) {
    if (isAbortLikeError(error)) throw error;
    console.warn(
      `[provider-directory] Firestore ${endpoint} failed; falling back to Events API`,
      error && error.message
    );
    return postPublicDirectoryEndpoint(endpoint, payload, signal);
  }
}

function base64UrlEncode(value) {
  const encoded = window.btoa(
    encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_match, hex) =>
      String.fromCharCode(parseInt(hex, 16)))
  );
  return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(value) {
  const padded = String(value || '').replace(/-/g, '+').replace(/_/g, '/')
    + '==='.slice((String(value || '').length + 3) % 4);
  const binary = window.atob(padded);
  const percentEncoded = Array.prototype.map.call(binary, (char) =>
    `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join('');
  return decodeURIComponent(percentEncoded);
}

function encodeCursor(sortName, docId) {
  if (!sortName || !docId) return null;
  return base64UrlEncode(JSON.stringify({ f: String(sortName), d: String(docId) }));
}

function decodeCursor(cursor) {
  if (!cursor || typeof cursor !== 'string') return null;
  try {
    const parsed = JSON.parse(base64UrlDecode(cursor));
    if (typeof parsed.f === 'string' && typeof parsed.d === 'string') return parsed;
    return null;
  } catch (e) {
    return null;
  }
}

function getDocumentIdFieldPath() {
  return firebaseMaster
    && firebaseMaster.firestore
    && firebaseMaster.firestore.FieldPath
    && typeof firebaseMaster.firestore.FieldPath.documentId === 'function'
    ? firebaseMaster.firestore.FieldPath.documentId()
    : '__name__';
}

function readString(value) {
  return typeof value === 'string' ? value : '';
}

function normalizeSpecialties(values) {
  return Array.isArray(values)
    ? values
      .filter((value) => typeof value === 'string')
      .map((value) => value.trim())
      .filter(Boolean)
    : [];
}

function truncateDescription(text) {
  if (typeof text !== 'string' || text.length === 0) return null;
  if (text.length <= 140) return text;
  return `${text.slice(0, 140).trimEnd()}...`;
}

function readPublishedDisplayName(provider) {
  const preferred = provider && typeof provider.preferredDisplayName === 'string'
    ? provider.preferredDisplayName.trim()
    : '';
  if (preferred) return preferred;
  const first = provider && typeof provider.firstName === 'string' ? provider.firstName.trim() : '';
  const last = provider && typeof provider.lastName === 'string' ? provider.lastName.trim() : '';
  return [first, last].filter(Boolean).join(' ').trim();
}

function extractParentProviderInfo(data) {
  const preferredDisplayName = typeof data?.preferredDisplayName === 'string'
    ? data.preferredDisplayName.trim()
    : '';
  const fullLegalName = typeof data?.fullLegalName === 'string'
    ? data.fullLegalName.trim()
    : '';
  const first = typeof data?.firstName === 'string' ? data.firstName.trim() : '';
  const last = typeof data?.lastName === 'string' ? data.lastName.trim() : '';
  const legalFirstName = typeof data?.legalFirstName === 'string' ? data.legalFirstName.trim() : '';
  const legalLastName = typeof data?.legalLastName === 'string' ? data.legalLastName.trim() : '';

  const name = preferredDisplayName
    || fullLegalName
    || [first, last].filter(Boolean).join(' ')
    || [legalFirstName, legalLastName].filter(Boolean).join(' ')
    || null;

  const logoUrl = typeof data?.profilePicUrl === 'string' && data.profilePicUrl
    ? data.profilePicUrl
    : (typeof data?.profilePhotoUrl === 'string' && data.profilePhotoUrl ? data.profilePhotoUrl : null);

  return { name, logoUrl };
}

function buildClinicFaviconUrl(websiteUrl) {
  if (typeof websiteUrl !== 'string' || !websiteUrl.trim()) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(websiteUrl.trim())}&sz=128`;
}

function extractClinicLogoUrl(clinic) {
  const explicitLogo = typeof clinic?.clinicLogoUrl === 'string' && clinic.clinicLogoUrl.trim()
    ? clinic.clinicLogoUrl.trim()
    : '';
  if (explicitLogo) return explicitLogo;
  return buildClinicFaviconUrl(clinic?.clinicWebsiteUrl);
}

function readFieldOfMedicine(provider) {
  const value = provider && provider.fieldOfMedicine;
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (value && typeof value === 'object') {
    if (typeof value.label === 'string' && value.label.length > 0) return value.label;
    if (typeof value.value === 'string' && value.value.length > 0) return value.value;
  }
  return null;
}

function readUserSpecialties(provider) {
  const fieldOfMedicine = readFieldOfMedicine(provider);
  const source = Array.isArray(provider && provider.specialtyCategories)
    ? provider.specialtyCategories
    : [];
  const deduped = new Map();

  source.forEach((item) => {
    if (typeof item !== 'string') return;
    const value = item.trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (!deduped.has(key)) deduped.set(key, value);
  });

  if (fieldOfMedicine) {
    const key = fieldOfMedicine.toLowerCase();
    if (!deduped.has(key)) deduped.set(key, fieldOfMedicine);
  }

  return Array.from(deduped.values());
}

function isProviderRole(role) {
  return role === 2;
}

function isListed(provider) {
  return provider && provider.directoryListed !== false;
}

function isProfileComplete(provider) {
  return provider && provider.isProfileSet === true;
}

function isProviderPayoutReady(provider) {
  return provider
    && (
      provider.payoutReady === true
      || (
        provider.payoutStatus === 'ready'
        && typeof provider.stripeConnectAccountId === 'string'
        && provider.stripeConnectAccountId.trim()
      )
    );
}

function flattenMasterSpecialtyCatalog(items) {
  const deduped = new Map();

  (Array.isArray(items) ? items : []).forEach((group) => {
    const services = Array.isArray(group && group.services) ? group.services : [];
    services.forEach((service) => {
      const name = service && typeof service.name === 'string' ? service.name.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (!deduped.has(key)) deduped.set(key, name);
    });
  });

  return Array.from(deduped.values()).sort((a, b) => a.localeCompare(b));
}

async function getMasterSpecialtyCatalog(signal) {
  throwIfAborted(signal);
  const snap = await masterDb.collection('fieldOfMedicines').get();
  throwIfAborted(signal);
  const rows = snap && Array.isArray(snap.docs)
    ? snap.docs.map((doc) => (doc && typeof doc.data === 'function' ? doc.data() : null))
    : [];
  return flattenMasterSpecialtyCatalog(rows);
}

function projectProjectedProvider(doc) {
  const data = doc.data() || {};
  const serviceAreas = Array.isArray(data.serviceAreas) ? data.serviceAreas : [];
  return {
    id: typeof data.id === 'string' && data.id ? data.id : doc.id,
    userId: typeof data.providerId === 'string' && data.providerId ? data.providerId : doc.id,
    firstName: readString(data.firstName),
    lastName: readString(data.lastName),
    displayName: readString(data.displayName),
    roleLabel: 'Team Member',
    profilePicUrl: typeof data.profilePicUrl === 'string' && data.profilePicUrl.length > 0
      ? data.profilePicUrl
      : null,
    profileDescriptionShort: typeof data.profileDescriptionShort === 'string'
      ? data.profileDescriptionShort
      : null,
    profileDescription: typeof data.profileDescription === 'string' && data.profileDescription.length > 0
      ? data.profileDescription
      : (typeof data.profileDescriptionShort === 'string' ? data.profileDescriptionShort : null),
    fieldOfMedicine: typeof data.fieldOfMedicine === 'string' && data.fieldOfMedicine.length > 0
      ? data.fieldOfMedicine
      : null,
    specialties: normalizeSpecialties(data.specialties),
    organisation: readString(data.organisation),
    serviceCoverageMode: data.serviceCoverageMode === 'all'
      ? 'all'
      : (serviceAreas.length > 0 ? 'defined' : 'all'),
    serviceAreas,
  };
}

function projectUserProvider(doc, clinicName) {
  const data = doc.data() || {};
  const serviceAreas = Array.isArray(data.serviceAreas) ? data.serviceAreas : [];
  return {
    id: doc.id,
    firstName: readString(data.firstName),
    lastName: readString(data.lastName),
    displayName: readPublishedDisplayName(data),
    roleLabel: 'Team Member',
    profilePicUrl: typeof data.profilePicUrl === 'string' && data.profilePicUrl.length > 0
      ? data.profilePicUrl
      : (typeof data.profilePhotoUrl === 'string' && data.profilePhotoUrl.length > 0
        ? data.profilePhotoUrl
        : null),
    profileDescriptionShort: truncateDescription(data.profileDescription || data.shortBio),
    profileDescription: typeof data.profileDescription === 'string' && data.profileDescription.length > 0
      ? data.profileDescription
      : (typeof data.shortBio === 'string' && data.shortBio.length > 0 ? data.shortBio : null),
    fieldOfMedicine: readFieldOfMedicine(data),
    specialties: readUserSpecialties(data),
    organisation: clinicName || '',
    serviceCoverageMode: data.serviceCoverageMode === 'all'
      ? 'all'
      : (serviceAreas.length > 0 ? 'defined' : 'all'),
    serviceAreas,
  };
}

function normalizeServiceCoverageFromData(data, fallbackMode = 'all', fallbackAreas = []) {
  const serviceAreas = Array.isArray(data?.serviceAreas) ? data.serviceAreas : fallbackAreas;
  const serviceCoverageMode = data?.serviceCoverageMode === 'all'
    ? 'all'
    : (serviceAreas.length > 0 ? 'defined' : fallbackMode);
  return {
    serviceCoverageMode,
    serviceAreas: serviceCoverageMode === 'all' ? [] : serviceAreas,
  };
}

function publicProvidersCollection(clinicId) {
  return firestoreOperationsDb
    .collection('publicProviderDirectory')
    .doc(clinicId)
    .collection('providers');
}

function makeProjectedMatcher({ q, location, specialties }) {
  const textNeedle = typeof q === 'string' ? q.trim().toLowerCase() : '';
  const hasStructuredLocation = location
    && typeof location === 'object'
    && Number.isFinite(Number(location.latitude))
    && Number.isFinite(Number(location.longitude));
  const selectedSpecialties = normalizeSpecialties(specialties).map((value) => value.toLowerCase());

  if (!textNeedle && !hasStructuredLocation && selectedSpecialties.length === 0) return null;

  return (data) => {
    if (textNeedle) {
      const searchTerms = Array.isArray(data.searchTerms) ? data.searchTerms : [];
      const searchable = searchTerms
        .concat(data.profileDescriptionShort)
        .filter((value) => typeof value === 'string')
        .map((value) => value.toLowerCase());
      if (!searchable.some((value) => value.includes(textNeedle))) return false;
    }

    if (hasStructuredLocation) {
      if (!matchesLocationFilter(data, location)) return false;
    }

    if (selectedSpecialties.length > 0) {
      const projectedSpecialties = normalizeSpecialties(data.specialties)
        .map((value) => value.toLowerCase());
      if (!selectedSpecialties.some((needle) => projectedSpecialties.includes(needle))) return false;
    }

    return true;
  };
}

function makeUserTextMatcher(q, clinicName) {
  const needle = typeof q === 'string' ? q.trim().toLowerCase() : '';
  if (!needle) return null;
  const clinicHaystack = (clinicName || '').toLowerCase();
  return (data) => {
    const publishedName = readPublishedDisplayName(data);
    if (publishedName && publishedName.toLowerCase().includes(needle)) return true;
    const profileDescription = truncateDescription(data.profileDescription || data.shortBio);
    if (profileDescription && profileDescription.toLowerCase().includes(needle)) return true;
    const fieldOfMedicine = readFieldOfMedicine(data);
    if (fieldOfMedicine && fieldOfMedicine.toLowerCase().includes(needle)) return true;
    const specialties = readUserSpecialties(data);
    if (specialties.some((specialty) => specialty.toLowerCase().includes(needle))) return true;
    return clinicHaystack.includes(needle);
  };
}

function makeUserLocationMatcher(location) {
  const hasStructuredLocation = location
    && typeof location === 'object'
    && Number.isFinite(Number(location.latitude))
    && Number.isFinite(Number(location.longitude));
  if (!hasStructuredLocation) return null;
  return (data) => matchesLocationFilter(data, location);
}

function makeUserSpecialtyMatcher(specialties) {
  const selected = normalizeSpecialties(specialties).map((value) => value.toLowerCase());
  if (selected.length === 0) return null;
  return (data) => {
    const providerSpecialties = readUserSpecialties(data).map((value) => value.toLowerCase());
    return selected.some((needle) => providerSpecialties.includes(needle));
  };
}

async function paginateProjectedProviders({
  clinicId,
  limit,
  q,
  location,
  specialties,
  cursorParts,
  signal,
}) {
  const matcher = makeProjectedMatcher({ q, location, specialties });
  const filtered = matcher !== null;
  const batchSize = filtered ? FILTERED_BATCH_SIZE : limit;
  const accumulated = [];
  let nextStart = cursorParts;
  let lastDoc = null;
  let batchesScanned = 0;

  while (accumulated.length < limit && batchesScanned < MAX_FILTERED_SCAN_BATCHES) {
    throwIfAborted(signal);
    let query = publicProvidersCollection(clinicId)
      .where('visibilityStatus', '==', 'public')
      .orderBy('sortName')
      .orderBy(getDocumentIdFieldPath());

    if (nextStart) {
      query = query.startAfter(nextStart.f, nextStart.d);
    }

    const snap = await query.limit(batchSize).get();
    throwIfAborted(signal);
    batchesScanned += 1;
    if (!snap || snap.empty) break;

    for (const doc of snap.docs) {
      lastDoc = doc;
      const data = doc.data() || {};
      if (!isProviderPayoutReady(data)) continue;
      if (matcher && !matcher(data)) continue;
      accumulated.push(projectProjectedProvider(doc));
      if (accumulated.length >= limit) break;
    }

    if (snap.docs.length < batchSize) {
      lastDoc = null;
      break;
    }

    if (accumulated.length >= limit) break;

    const tail = snap.docs[snap.docs.length - 1];
    const tailData = tail.data() || {};
    nextStart = { f: tailData.sortName || '', d: tail.id };
  }

  let nextCursor = null;
  if (lastDoc) {
    const tailData = lastDoc.data() || {};
    nextCursor = encodeCursor(tailData.sortName || '', lastDoc.id);
  }

  const providers = await hydrateProjectedProviderServiceCoverage(accumulated, signal);

  return {
    providers,
    nextCursor,
    scannedCount: providers.length,
    source: 'projection',
  };
}

async function hydrateProjectedProviderServiceCoverage(providers, signal) {
  if (!Array.isArray(providers) || providers.length === 0) return providers;
  const hydrated = await Promise.all(providers.map(async (provider) => {
    const userId = typeof provider?.userId === 'string' ? provider.userId.trim() : '';
    if (!userId) return provider;
    try {
      throwIfAborted(signal);
      const snap = await firestoreOperationsDb.collection('users').doc(userId).get();
      throwIfAborted(signal);
      if (!snap || !snap.exists) return provider;
      const data = snap.data() || {};
      if (!Array.isArray(data.serviceAreas) && typeof data.serviceCoverageMode !== 'string') return provider;
      return {
        ...provider,
        ...normalizeServiceCoverageFromData(
          data,
          provider.serviceCoverageMode,
          Array.isArray(provider.serviceAreas) ? provider.serviceAreas : []
        ),
      };
    } catch (error) {
      if (error && error.name === 'AbortError') throw error;
      return provider;
    }
  }));
  return hydrated;
}

async function buildSuspendedProviderIdSet(accumulatedMeta) {
  const wwuEntries = accumulatedMeta.filter((item) => item.providerId);
  if (wwuEntries.length === 0) return new Set();

  try {
    const snaps = await Promise.all(
      wwuEntries.map((item) => firestoreOperationsDb.collection('providers').doc(item.providerId).get())
    );
    const suspended = new Set();
    snaps.forEach((snap) => {
      if (snap.exists && (snap.data() || {}).status === 'SUSPENDED') {
        suspended.add(snap.id);
      }
    });
    return suspended;
  } catch (error) {
    console.warn('[provider-directory] suspension check failed', error && error.message);
    return new Set();
  }
}

async function getParentProviderInfo(clinicId, adminUserId, signal) {
  if (!clinicId) return { name: null, logoUrl: null };

  if (typeof adminUserId === 'string' && adminUserId.trim()) {
    try {
      throwIfAborted(signal);
      const adminSnap = await firestoreOperationsDb
        .collection('users')
        .doc(adminUserId.trim())
        .get();
      throwIfAborted(signal);
      if (adminSnap.exists) {
        const admin = adminSnap.data() || {};
        if (admin.clinicId === clinicId && admin.isClinicAdmin === true) {
          return extractParentProviderInfo(admin);
        }
      }
    } catch (error) {
      if (error && error.name === 'AbortError') throw error;
      console.warn('[provider-directory] direct parent provider lookup failed', clinicId, error && error.message);
    }
  }

  try {
    throwIfAborted(signal);
    const snapshot = await firestoreOperationsDb
      .collection('users')
      .where('clinicId', '==', clinicId)
      .where('isClinicAdmin', '==', true)
      .limit(1)
      .get();
    throwIfAborted(signal);
    if (!snapshot || snapshot.empty) return { name: null, logoUrl: null };
    return extractParentProviderInfo(snapshot.docs[0].data() || {});
  } catch (error) {
    if (error && error.name === 'AbortError') throw error;
    console.warn('[provider-directory] parent provider lookup failed', clinicId, error && error.message);
    return { name: null, logoUrl: null };
  }
}

async function paginateUserProviders({
  clinicId,
  clinicName,
  clinicAddress,
  limit,
  q,
  location,
  specialties,
  cursorParts,
  signal,
}) {
  const textMatcher = makeUserTextMatcher(q, clinicName);
  const locationMatcher = makeUserLocationMatcher(location);
  const specialtyMatcher = makeUserSpecialtyMatcher(specialties);
  const matcher = (textMatcher || locationMatcher || specialtyMatcher)
    ? (data) => {
      if (textMatcher && !textMatcher(data)) return false;
      if (locationMatcher && !locationMatcher(data, clinicAddress)) return false;
      if (specialtyMatcher && !specialtyMatcher(data)) return false;
      return true;
    }
    : null;
  const filtered = matcher !== null;
  const batchSize = filtered ? FILTERED_BATCH_SIZE : limit;
  const accumulated = [];
  const accumulatedMeta = [];
  let nextStart = cursorParts;
  let lastDoc = null;
  let batchesScanned = 0;

  while (accumulated.length < limit && batchesScanned < MAX_FILTERED_SCAN_BATCHES) {
    throwIfAborted(signal);
    let query = firestoreOperationsDb
      .collection('users')
      .where('clinicId', '==', clinicId)
      .orderBy('firstName')
      .orderBy(getDocumentIdFieldPath());

    if (nextStart) {
      query = query.startAfter(nextStart.f, nextStart.d);
    }

    const snap = await query.limit(batchSize).get();
    throwIfAborted(signal);
    batchesScanned += 1;
    if (!snap || snap.empty) break;

    for (const doc of snap.docs) {
      lastDoc = doc;
      const data = doc.data() || {};
      if (!isProviderRole(data.role)) continue;
      if (!isListed(data)) continue;
      if (!isProfileComplete(data)) continue;
      if (!isProviderPayoutReady(data)) continue;
      if (matcher && !matcher(data)) continue;
      accumulated.push(projectUserProvider(doc, clinicName));
      accumulatedMeta.push({
        providerId: data.isProvider && typeof data.providerId === 'string' && data.providerId
          ? data.providerId
          : null,
      });
      if (accumulated.length >= limit) break;
    }

    if (snap.docs.length < batchSize) {
      lastDoc = null;
      break;
    }

    if (accumulated.length >= limit) break;

    const tail = snap.docs[snap.docs.length - 1];
    const tailData = tail.data() || {};
    nextStart = { f: tailData.firstName || '', d: tail.id };
  }

  const suspendedProviderIds = await buildSuspendedProviderIdSet(accumulatedMeta);
  const providers = suspendedProviderIds.size > 0
    ? accumulated.filter((_, index) => !suspendedProviderIds.has(accumulatedMeta[index].providerId))
    : accumulated;

  let nextCursor = null;
  if (lastDoc) {
    const tailData = lastDoc.data() || {};
    nextCursor = encodeCursor(tailData.firstName || '', lastDoc.id);
  }

  return {
    providers,
    nextCursor,
    source: 'users',
  };
}

async function paginateProvidersPreferProjection(input) {
  try {
    const projected = await paginateProjectedProviders(input);
    if (projected.scannedCount > 0 || input.cursorParts) {
      return projected;
    }
  } catch (error) {
    console.warn('[provider-directory] projected provider page unavailable; falling back', input.clinicId, error && error.message);
  }

  return paginateUserProviders(input);
}

async function evaluateDirectoryAccess({ clinicId, token, signal }) {
  throwIfAborted(signal);
  const clinicSnap = await firestoreOperationsDb.collection('clinics').doc(clinicId).get();
  throwIfAborted(signal);
  if (!clinicSnap.exists) {
    return {
      status: 'clinic_not_found',
      clinicName: null,
      clinicLocation: '',
      directoryEnabledGlobally: false,
      directoryEnabled: false,
      parentProviderUserId: null,
    };
  }

  const clinic = clinicSnap.data() || {};
  const directoryEnabledGlobally = clinic.directoryEnabledGlobally === true;
  const directoryEnabled = clinic.directoryEnabled === true;

  if (!directoryEnabledGlobally || !directoryEnabled) {
    return {
      status: 'directory_disabled',
      clinicName: null,
      clinicLocation: '',
      directoryEnabledGlobally,
      directoryEnabled,
      parentProviderUserId: null,
    };
  }

  if (typeof token !== 'string' || !token || token !== clinic.embedToken) {
    return {
      status: 'access_denied',
      clinicName: null,
      clinicLocation: '',
      directoryEnabledGlobally,
      directoryEnabled,
      parentProviderUserId: null,
    };
  }

  return {
    status: 'ok',
    clinicName: typeof clinic.clinicName === 'string' ? clinic.clinicName : null,
    clinicLocation: typeof clinic.clinicAddress === 'string' ? clinic.clinicAddress.trim() : '',
    clinicLogoUrl: extractClinicLogoUrl(clinic),
    directoryEnabledGlobally,
    directoryEnabled,
    parentProviderUserId: typeof clinic.userId === 'string' ? clinic.userId : null,
  };
}

function normalizeBootstrapPayload(payload) {
  const clinicId = typeof payload?.clinicId === 'string' ? payload.clinicId.trim() : '';
  const token = typeof payload?.token === 'string' ? payload.token.trim() : '';
  if (!clinicId || !token) return null;
  return {
    clinicId,
    token,
    limit: Number.isFinite(payload?.limit) ? payload.limit : PUBLIC_DIRECTORY_PAGE_SIZE,
    includeSpecialties: payload?.includeSpecialties !== false,
    includeBranding: payload?.includeBranding !== false,
  };
}

function buildBootstrapPrefetchKey(payload) {
  const normalized = normalizeBootstrapPayload(payload);
  return normalized ? JSON.stringify(normalized) : '';
}

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}

export function getPublicDirectoryBootstrapPayloadFromLocation(locationLike) {
  const pathname = typeof locationLike?.pathname === 'string' ? locationLike.pathname : '';
  const match = pathname.match(/^\/provider-directory\/embed\/([^/?#]+)\/?$/);
  if (!match) return null;

  let token = '';
  try {
    token = new URLSearchParams(locationLike?.search || '').get('t') || '';
  } catch (e) {
    token = '';
  }

  const clinicId = safeDecode(match[1]).trim();
  token = token.trim();
  if (!clinicId || !token) return null;

  return {
    clinicId,
    token,
    limit: PUBLIC_DIRECTORY_PAGE_SIZE,
    includeSpecialties: false,
    includeBranding: false,
  };
}

export function prefetchDirectoryBootstrap(payload) {
  const normalized = normalizeBootstrapPayload(payload);
  const key = buildBootstrapPrefetchKey(normalized);
  if (!normalized || !key) return null;
  if (directoryBootstrapPrefetch.has(key)) {
    return directoryBootstrapPrefetch.get(key);
  }

  const promise = bootstrapDirectory(normalized);
  directoryBootstrapPrefetch.set(key, promise);
  promise.catch(() => {
    directoryBootstrapPrefetch.delete(key);
  });
  return promise;
}

export function consumePrefetchedDirectoryBootstrap(payload) {
  const key = buildBootstrapPrefetchKey(payload);
  if (!key || !directoryBootstrapPrefetch.has(key)) return null;
  const promise = directoryBootstrapPrefetch.get(key);
  directoryBootstrapPrefetch.delete(key);
  return promise;
}

export function startPublicDirectoryBootstrapPrefetchFromLocation(locationLike) {
  const payload = getPublicDirectoryBootstrapPayloadFromLocation(locationLike);
  return payload ? prefetchDirectoryBootstrap(payload) : null;
}

export function clearDirectoryBootstrapPrefetchForTests() {
  directoryBootstrapPrefetch.clear();
}

/**
 * @param {Object} payload - { clinicId, token }
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, status?: string, data?: Object, error?: string }>}
 */
async function validateDirectoryTokenFromFirestore(payload, signal) {
  const clinicId = typeof payload?.clinicId === 'string' ? payload.clinicId.trim() : '';
  if (!clinicId) return { success: false, error: 'clinic_id_required' };
  const access = await evaluateDirectoryAccess({
    clinicId,
    token: typeof payload?.token === 'string' ? payload.token : '',
    signal,
  });
  const parentProviderInfo = access.status === 'ok'
    ? await getParentProviderInfo(clinicId, access.parentProviderUserId, signal)
    : { name: null, logoUrl: null };
  return {
    success: true,
    status: access.status,
    data: {
      clinicName: access.status === 'ok' ? access.clinicName : null,
      clinicLogoUrl: access.status === 'ok' ? access.clinicLogoUrl : null,
      parentProviderName: parentProviderInfo.name || null,
      clinicLocation: access.status === 'ok' ? access.clinicLocation : '',
      directoryEnabledGlobally: access.directoryEnabledGlobally,
      parentProviderLogoUrl: parentProviderInfo.logoUrl || null,
    },
  };
}

export async function validateDirectoryToken(payload, signal) {
  return withEventsApiFallback(
    'validateDirectoryToken',
    payload,
    signal,
    () => validateDirectoryTokenFromFirestore(payload, signal)
  );
}

/**
 * @param {Object} payload - { clinicId, token, limit? }
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, status?: string, data?: Object, error?: string }>}
 */
async function bootstrapDirectoryFromFirestore(payload, signal) {
  const clinicId = typeof payload?.clinicId === 'string' ? payload.clinicId.trim() : '';
  if (!clinicId) return { success: false, error: 'clinic_id_required' };

  const access = await evaluateDirectoryAccess({
    clinicId,
    token: typeof payload?.token === 'string' ? payload.token : '',
    signal,
  });
  if (access.status !== 'ok') {
    return {
      success: true,
      status: access.status,
      data: {},
    };
  }

  const safeLimit = clampLimit(payload?.limit);
  const shouldIncludeSpecialties = payload?.includeSpecialties !== false;
  const shouldIncludeBranding = payload?.includeBranding !== false;
  const [{ providers, nextCursor }, availableSpecialties, parentProviderInfo] = await Promise.all([
    paginateProvidersPreferProjection({
      clinicId,
      clinicName: access.clinicName || '',
      clinicAddress: access.clinicLocation || '',
      limit: safeLimit,
      q: '',
      location: '',
      specialties: [],
      cursorParts: null,
      signal,
    }),
    shouldIncludeSpecialties ? getMasterSpecialtyCatalog(signal) : Promise.resolve(null),
    shouldIncludeBranding
      ? getParentProviderInfo(clinicId, access.parentProviderUserId, signal)
      : Promise.resolve({ name: null, logoUrl: null }),
  ]);

  const data = {
    directoryName: access.clinicName || '',
    clinicName: access.clinicName || '',
    clinicLogoUrl: access.clinicLogoUrl || null,
    parentProviderName: parentProviderInfo.name || null,
    parentProviderLogoUrl: parentProviderInfo.logoUrl || null,
    clinicLocation: access.clinicLocation || '',
    directoryStatus: 'enabled',
    providers,
    nextCursor,
  };
  if (shouldIncludeSpecialties) {
    data.availableSpecialties = availableSpecialties;
  }

  return {
    success: true,
    status: 'ok',
    data,
  };
}

export async function bootstrapDirectory(payload, signal) {
  return withEventsApiFallback(
    'bootstrapDirectory',
    payload,
    signal,
    () => bootstrapDirectoryFromFirestore(payload, signal)
  );
}

/**
 * @param {Object} payload - { clinicId, token, q?, limit?, cursor? }
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, status?: string, data?: Object, error?: string }>}
 */
async function listDirectoryProvidersFromFirestore(payload, signal) {
  const clinicId = typeof payload?.clinicId === 'string' ? payload.clinicId.trim() : '';
  if (!clinicId) return { success: false, error: 'clinic_id_required' };

  const access = await evaluateDirectoryAccess({
    clinicId,
    token: typeof payload?.token === 'string' ? payload.token : '',
    signal,
  });
  if (access.status !== 'ok') {
    return {
      success: true,
      status: access.status,
      data: {},
    };
  }

  const specialties = Array.isArray(payload?.specialties)
    ? payload.specialties
    : (typeof payload?.specialty === 'string' && payload.specialty.trim() ? [payload.specialty] : []);
  const safeLimit = clampLimit(payload?.limit);
  const cursorParts = decodeCursor(payload?.cursor);
  const isInitialLoad = !cursorParts;
  const shouldIncludeSpecialties = payload?.includeSpecialties !== false;
  const [{ providers, nextCursor }, availableSpecialties] = await Promise.all([
    paginateProvidersPreferProjection({
      clinicId,
      clinicName: access.clinicName || '',
      clinicAddress: access.clinicLocation || '',
      limit: safeLimit,
      q: typeof payload?.q === 'string' ? payload.q : '',
      location: payload?.location || '',
      specialties,
      cursorParts,
      signal,
    }),
    isInitialLoad && shouldIncludeSpecialties
      ? getMasterSpecialtyCatalog(signal)
      : Promise.resolve(null),
  ]);

  const data = {
    clinicName: access.clinicName,
    clinicLogoUrl: access.clinicLogoUrl || null,
    parentProviderLogoUrl: null,
    providers,
    nextCursor,
  };
  if (isInitialLoad) {
    data.parentProviderName = null;
    data.clinicLocation = access.clinicLocation || '';
    if (shouldIncludeSpecialties) {
      data.availableSpecialties = availableSpecialties;
    }
  }

  return {
    success: true,
    status: 'ok',
    data,
  };
}

export async function listDirectoryProviders(payload, signal) {
  return withEventsApiFallback(
    'listDirectoryProviders',
    payload,
    signal,
    () => listDirectoryProvidersFromFirestore(payload, signal)
  );
}

/**
 * Verify a reCAPTCHA v3 token against the backend before opening the Connect flow.
 *
 * The backend endpoint (verifyMarketplaceRecaptcha) is public — no Firebase
 * ID token required. It calls Google's siteverify API and returns
 * { success, score, action?, error? }.
 *
 * @param {Object} payload - { token: string, action?: string }
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, score?: number, action?: string, error?: string }>}
 */
export async function verifyMarketplaceRecaptcha(payload, signal) {
  const res = await axios.post(`${url}verifyMarketplaceRecaptcha`, payload, { signal });
  return res.data;
}

export async function resolvePatientConnection(payload, signal) {
  return postPublicDirectoryEndpoint(
    'resolvePatientConnection',
    payload,
    signal,
    PUBLIC_DIRECTORY_CONFIRM_TIMEOUT_MS
  );
}

export async function confirmPatientConnection(payload, signal) {
  return postPublicDirectoryEndpoint(
    'confirmPatientConnection',
    payload,
    signal,
    PUBLIC_DIRECTORY_CONFIRM_TIMEOUT_MS
  );
}
